/**
 * Encoder do BWS LL NB IoT+LoRa — comandos de escrita conforme "Lista de comandos" do
 * manual v1.0.0. Todo comando termina em \r\n; campos ausentes (undefined) não geram comando.
 */

type APN = {
  address: string;
  user: string;
  password?: string;
};

type IP = {
  ip: string;
  port: number;
};

type DNS = {
  address: string;
  port: number;
};

type RadioBehavior = {
  no_connection_timeout: number;
  stopped_cycle: number;
  moving_cycle: number;
  flags: number;
};

type Encoder =
  | { command: 'apn'; args: APN }
  | { command: 'ip_primary'; args: IP }
  | { command: 'ip_secondary'; args: IP }
  | { command: 'dns_primary'; args: DNS }
  | { command: 'dns_secondary'; args: DNS }
  | { command: 'radio_behavior'; args: RadioBehavior };

/** Faixa de 2 bytes aceita pelo firmware (0 à 65535). */
const isUint16 = (input: unknown): input is number =>
  typeof input === 'number' && Number.isInteger(input) && input >= 0 && input <= 65535;

/** Intervalos com mínimo de 1 (ex.: tempo em movimento/parado — "Tempo Mínimo 1 minuto"). */
const isPositiveUint16 = (input: unknown): input is number => isUint16(input) && input >= 1;

/** Valores de "z" (flags) da tabela da seção "Comportamento do Rádio". */
export const LL_NB_RADIO_FLAGS = [37, 41, 45] as const;

/** Valores aceitos pelo WFOM: estoque = 1, normal = 2, desativado = 128. */
export const LL_NB_WORK_MODES = [1, 2, 128] as const;

export class BwsLlNbEncoder {
  static serial(input: string): string | undefined {
    if (!input || typeof input !== 'string') return undefined;
    return `WINS=${input}\r\n`;
  }

  /** WIAP=x,y,z — x = APN, y = usuário, z = senha. */
  static apn(input: APN): string | undefined {
    if (!input?.address || !input.user) return undefined;
    return `WIAP=${input.address},${input.user},${input.password ?? ''}\r\n`;
  }

  /** WIP1=x,y — x = IP1, y = porta. */
  static ip_primary(input: IP): string | undefined {
    if (!input?.ip || !isUint16(input.port)) return undefined;
    return `WIP1=${input.ip},${input.port}\r\n`;
  }

  /** WIP2=x,y — x = IP2, y = porta. */
  static ip_secondary(input: IP): string | undefined {
    if (!input?.ip || !isUint16(input.port)) return undefined;
    return `WIP2=${input.ip},${input.port}\r\n`;
  }

  /** WID1=x,y — x = DNS1, y = porta. */
  static dns_primary(input: DNS): string | undefined {
    if (!input?.address || !isUint16(input.port)) return undefined;
    return `WID1=${input.address},${input.port}\r\n`;
  }

  /** WID2=x,y — x = DNS2, y = porta. */
  static dns_secondary(input: DNS): string | undefined {
    if (!input?.address || !isUint16(input.port)) return undefined;
    return `WID2=${input.address},${input.port}\r\n`;
  }

  /** WCM= — tempo de posição em movimento, em minutos (1 à 65535). Padrão 5. */
  static data_transmission_moving(input: number): string | undefined {
    if (!isPositiveUint16(input)) return undefined;
    return `WCM=${input}\r\n`;
  }

  /** WCW= — tempo de posição parado, em minutos (1 à 65535). */
  static data_transmission_stopped(input: number): string | undefined {
    if (!isPositiveUint16(input)) return undefined;
    return `WCW=${input}\r\n`;
  }

  /** WCE= — tempo de posição em modo SOS, em segundos (0 à 65535). Padrão 60. */
  static data_transmission_sos(input: number): string | undefined {
    if (!isUint16(input)) return undefined;
    return `WCE=${input}\r\n`;
  }

  /** WCTE= — tempo em modo estoque, em minutos. */
  static data_transmission_stock(input: number): string | undefined {
    if (!isUint16(input)) return undefined;
    return `WCTE=${input}\r\n`;
  }

  /** WCD= — tempo de atualização em modo desabilitado, em minutos (0 à 65535). Padrão 1440. */
  static data_transmission_disabled(input: number): string | undefined {
    if (!isUint16(input)) return undefined;
    return `WCD=${input}\r\n`;
  }

  /** WFGO= — liga o GPS em cada ciclo (1 habilita, 0 desabilita). */
  static gps_every_cycle(input: boolean): string | undefined {
    if (typeof input !== 'boolean') return undefined;
    return `WFGO=${input ? 1 : 0}\r\n`;
  }

  /** WCG= — tempo aguardando FIX de GPS, em segundos (0 à 65535). Padrão 120. */
  static gps_fix_timeout(input: number): string | undefined {
    if (!isUint16(input)) return undefined;
    return `WCG=${input}\r\n`;
  }

  /** WFOM= — modo de trabalho: estoque = 1, normal = 2, desativado = 128. */
  static work_mode(input: number): string | undefined {
    if (!(LL_NB_WORK_MODES as readonly number[]).includes(input)) return undefined;
    return `WFOM=${input}\r\n`;
  }

  /** WFST= — time out de SOS (SOS temporizado), em minutos (máx. 65535). Padrão 60. */
  static sos_timeout(input: number): string | undefined {
    if (!isUint16(input)) return undefined;
    return `WFST=${input}\r\n`;
  }

  /** WFPO= — envio da string de potência (1 habilita, 0 desabilita). */
  static power_string(input: boolean): string | undefined {
    if (typeof input !== 'boolean') return undefined;
    return `WFPO=${input ? 1 : 0}\r\n`;
  }

  /** WFGM= — ativa/desativa o modo SOS via serial (1 habilita, 0 desabilita). */
  static sos_mode(input: boolean): string | undefined {
    if (typeof input !== 'boolean') return undefined;
    return `WFGM=${input ? 1 : 0}\r\n`;
  }

  /**
   * WBTR=w,x,y,z — comportamento do rádio LoRa.
   * w: tempo máximo sem conexão (min, 1 à 65535); x: ciclo LoRa parado; y: ciclo LoRa em
   * movimento; z: flags (37 jammer, 41 sem cobertura, 45 jammer + sem cobertura).
   */
  static radio_behavior(input: RadioBehavior): string | undefined {
    if (!input) return undefined;
    const { no_connection_timeout, stopped_cycle, moving_cycle, flags } = input;
    if (
      !isPositiveUint16(no_connection_timeout) ||
      !isPositiveUint16(stopped_cycle) ||
      !isPositiveUint16(moving_cycle) ||
      !(LL_NB_RADIO_FLAGS as readonly number[]).includes(flags)
    ) {
      return undefined;
    }
    return `WBTR=${no_connection_timeout},${stopped_cycle},${moving_cycle},${flags}\r\n`;
  }

  /** DN / DF — liga/desliga o log de debug. */
  static debug(input: boolean): string | undefined {
    if (typeof input !== 'boolean') return undefined;
    return input ? 'DN\r\n' : 'DF\r\n';
  }

  /**
   * Comandos aplicáveis a partir de um perfil de configuração. `sos_mode` e `debug` ficam de
   * fora de propósito: são ações operacionais (via plataforma/terminal), não configuração.
   */
  static commands() {
    return {
      apn: BwsLlNbEncoder.apn,
      ip_primary: BwsLlNbEncoder.ip_primary,
      ip_secondary: BwsLlNbEncoder.ip_secondary,
      dns_primary: BwsLlNbEncoder.dns_primary,
      dns_secondary: BwsLlNbEncoder.dns_secondary,
      data_transmission_moving: BwsLlNbEncoder.data_transmission_moving,
      data_transmission_stopped: BwsLlNbEncoder.data_transmission_stopped,
      data_transmission_sos: BwsLlNbEncoder.data_transmission_sos,
      data_transmission_stock: BwsLlNbEncoder.data_transmission_stock,
      data_transmission_disabled: BwsLlNbEncoder.data_transmission_disabled,
      gps_every_cycle: BwsLlNbEncoder.gps_every_cycle,
      gps_fix_timeout: BwsLlNbEncoder.gps_fix_timeout,
      work_mode: BwsLlNbEncoder.work_mode,
      sos_timeout: BwsLlNbEncoder.sos_timeout,
      power_string: BwsLlNbEncoder.power_string,
      radio_behavior: BwsLlNbEncoder.radio_behavior,
    };
  }

  static encoder(props: Encoder | { command: string; args: unknown }): string | undefined {
    const { command, args } = props;
    const commands = BwsLlNbEncoder.commands();
    const fn = commands[command as keyof ReturnType<typeof BwsLlNbEncoder.commands>];
    // Ignora chaves do profile sem comando serial correspondente (ex.: technology_system_name).
    if (typeof fn !== 'function') return undefined;
    return fn(args as never);
  }
}
